import { useEffect, useRef } from 'react';

import { useGiftWrapContext } from '../../context/GiftWrapContext';

/**
 * Custom hook for PersonalisationModal logic and className selection.
 * @param {Object} options - Options object.
 * @param {boolean} options.isOpen - Whether the modal is open.
 * @param {function} options.onClose - Callback for closing the modal.
 * @returns {Object} View model for the modal component.
 */
export function useViewModel({ isOpen, onClose }) {
  const { resetGiftWrapState } = useGiftWrapContext();

  // Handle personalisation modal dialog.
  /** @type {React.RefObject<HTMLDialogElement | null>} */
  const dialogModalRef = useRef(null);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      // Use requestAnimationFrame instead of setTimeout for smoother animation.
      requestAnimationFrame(() => {
        dialog.setAttribute('data-open', 'true');
      });
    } else if (!isOpen && dialog.open) {
      dialog.setAttribute('data-open', 'false');
      // Use requestAnimationFrame for smoother closing animation.
      requestAnimationFrame(() => {
        dialog.close();
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      if (!isOpen) return;
      dialog.setAttribute('data-open', 'false');
      // Use requestAnimationFrame for smoother state reset.
      requestAnimationFrame(() => {
        // Reset all gift wrap state when modal is closed natively.
        resetGiftWrapState();
        onClose?.();
      });
    };

    dialog.addEventListener('close', handleNativeClose);

    return () => {
      dialog.removeEventListener('close', handleNativeClose);
    };
  }, [isOpen, onClose, resetGiftWrapState]);

  const handleClose = () => {
    // Reset all gift wrap state when modal is closed.
    resetGiftWrapState();

    if (onClose) {
      onClose();
    }
  };

  return {
    dialogModalRef,
    handleClose
  };
}
